import { onMounted, onUnmounted, ref, unref, watch } from 'vue';

import { useEventListener } from '@resn/gozer-vue';

export const usePinch = (
    el = ref(null),
    {
        onPinchStart = () => {},
        onPinchMove = () => {},
        onPinchEnd = () => {},
        enableOnMount = true,
    } = {}
) => {
    const isPinching = ref(false);
    const isRotating = ref(false);
    const isEnabled = ref(enableOnMount);

    const activePointers = new Map();
    let initialDistance = 0;
    let initialAngle = 0;
    let previousDistance = 0;
    let previousAngle = 0;

    const getPointers = () => {
        return Array.from(activePointers.values());
    };

    const getDistance = (pointer1, pointer2) => {
        const dx = pointer2.clientX - pointer1.clientX;
        const dy = pointer2.clientY - pointer1.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    const getAngle = (pointer1, pointer2) => {
        const dx = pointer2.clientX - pointer1.clientX;
        const dy = pointer2.clientY - pointer1.clientY;
        return Math.atan2(dy, dx);
    };

    const getCenter = (pointer1, pointer2) => {
        return {
            x: (pointer1.clientX + pointer2.clientX) / 2,
            y: (pointer1.clientY + pointer2.clientY) / 2,
        };
    };

    const handlePointerDown = (event) => {
        activePointers.set(event.pointerId, {
            pointerId: event.pointerId,
            clientX: event.clientX,
            clientY: event.clientY,
        });

        if (activePointers.size === 2) {
            const pointers = getPointers();
            initialDistance = getDistance(pointers[0], pointers[1]);
            initialAngle = getAngle(pointers[0], pointers[1]);
            previousDistance = initialDistance;
            previousAngle = initialAngle;

            isPinching.value = true;
            onPinchStart({
                center: getCenter(pointers[0], pointers[1]),
                distance: initialDistance,
                angle: initialAngle,
            });
        }
    };

    const handlePointerMove = (event) => {
        if (activePointers.has(event.pointerId)) {
            activePointers.set(event.pointerId, {
                pointerId: event.pointerId,
                clientX: event.clientX,
                clientY: event.clientY,
            });

            if (isPinching.value && activePointers.size === 2) {
                event.preventDefault();
                const pointers = getPointers();

                const currentDistance = getDistance(pointers[0], pointers[1]);
                const currentAngle = getAngle(pointers[0], pointers[1]);

                const zoomDelta = currentDistance - previousDistance;
                const rotateDelta = currentAngle - previousAngle;

                previousDistance = currentDistance;
                previousAngle = currentAngle;

                onPinchMove({
                    center: getCenter(pointers[0], pointers[1]),
                    distance: currentDistance,
                    angle: currentAngle,
                    zoomDelta,
                    rotateDelta,
                });
            }
        }
    };

    const handlePointerUp = (event) => {
        activePointers.delete(event.pointerId);

        if (isPinching.value && activePointers.size < 2) {
            isPinching.value = false;
            isRotating.value = false;
            activePointers.clear();
            initialDistance = 0;
            initialAngle = 0;
            previousDistance = 0;
            previousAngle = 0;

            onPinchEnd();
        }
    };

    // useEventListener(el, 'pointerdown', handlePointerDown);
    // useEventListener(el, 'pointermove', handlePointerMove);
    // useEventListener(el, 'pointerup', handlePointerUp);
    // useEventListener(el, 'pointercancel', handlePointerUp);

    const enable = () => {
        isEnabled.value = true;
    };

    const disable = () => {
        isEnabled.value = false;
    };

    const setEnabled = () => {
        if (isEnabled.value) {
            addListeners();
        } else {
            removeListeners();
        }
    };

    const resetState = () => {
        activePointers.clear();
        isPinching.value = false;
        isRotating.value = false;
        initialDistance = 0;
        initialAngle = 0;
        previousDistance = 0;
        previousAngle = 0;
    };

    const addListeners = () => {
        const element = unref(el);
        if (!element) return;

        resetState();

        element.addEventListener('pointerdown', handlePointerDown, { passive: false });
        element.addEventListener('pointermove', handlePointerMove, { passive: false });
        element.addEventListener('pointerup', handlePointerUp);
        element.addEventListener('pointercancel', handlePointerUp);
    };

    const removeListeners = () => {
        const element = unref(el);
        if (!element) return;

        element.removeEventListener('pointerdown', handlePointerDown);
        element.removeEventListener('pointermove', handlePointerMove);
        element.removeEventListener('pointerup', handlePointerUp);
        element.removeEventListener('pointercancel', handlePointerUp);

        resetState();
    };

    onMounted(() => {
        if (isEnabled.value) addListeners();
    });

    onUnmounted(() => {
        removeListeners();
    });

    watch(isEnabled, setEnabled);

    return {
        isPinching,
        isEnabled,
        enable,
        disable,
    };
};
